"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, ShieldCheck, Sparkles, ChartBar as BarChart3, Wrench, FileText, Link2, Calendar } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const deliverables = [{
  title: "Monthly SEO strategy",
  description: "Targeted keyword priorities and conversion-led roadmaps tailored to your commercial goals.",
  icon: BarChart3
}, {
  title: "Ongoing technical SEO",
  description: "Continuous health audits, crawl budget optimization, and site speed enhancements.",
  icon: Wrench
}, {
  title: "Ongoing content optimisation",
  description: "Refining existing pages and crafting high-intent content assets that rank and convert.",
  icon: FileText
}, {
  title: "Ongoing link building",
  description: "High-quality, genuine digital PR and authoritative editorial placements.",
  icon: Link2
}, {
  title: "Monthly SEO reporting",
  description: "Honest, jargon-free performance reviews showing traffic, rankings, and lead impact.",
  icon: Calendar
}];
export default function MonthlyHero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="monthlyseohero" data-nav="light" className="relative bg-background text-foreground pt-32 pb-20 md:pt-36 md:pb-28 overflow-hidden border-b border-border">
      {/* Decorative ambient background accents */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full overflow-hidden pointer-events-none -z-10">
        <div className="absolute -top-24 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute top-1/3 -left-20 w-80 h-80 bg-accent/10 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <motion.div variants={containerVariants} initial="hidden" animate="show" className="flex flex-col items-center text-center space-y-8">
          {/* Trust Eyebrow Badge */}
          <motion.div variants={itemVariants}>
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-secondary text-secondary-foreground border border-border shadow-xs text-xs sm:text-sm font-medium">
              <ShieldCheck className="size-4 text-primary shrink-0" />
              <span>Flexible monthly retainer — dedicated monthly hours on a rolling 30-day agreement</span>
            </div>
          </motion.div>

          {/* Main H1 Title */}
          <motion.div variants={itemVariants} className="max-w-4xl space-y-4">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif font-bold tracking-tight text-foreground leading-[1.15]">
              Monthly SEO services built for steady search growth
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed font-sans">
              Each month runs on a structured retainer sprint with allocated hours for technical fixes, content optimisation, and link acquisition. You receive agreed monthly roadmaps, measurable search progress, and rolling 30-day terms with no lock-in.
            </p>
          </motion.div>

          {/* Primary & Secondary Call to Actions */}
          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full sm:w-auto">
            <Button asChild size="lg" className="w-full sm:w-auto px-7 py-6 text-base font-medium shadow-md">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto px-7 py-6 text-base font-medium">
              <Link to="/SeoAuditsPage">
                Get a Free SEO Audit
              </Link>
            </Button>
          </motion.div>

          {/* Fast Guarantee Strip */}
          <motion.div variants={itemVariants} className="pt-2">
            <div className="flex flex-wrap items-center justify-center gap-y-2 gap-x-6 text-xs sm:text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="size-4 text-primary" /> Rolling monthly agreements
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="size-4 text-primary" /> Clear deliverable roadmaps
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="size-4 text-primary" /> Direct WhatsApp and email access
              </span>
            </div>
          </motion.div>

          {/* Monthly Inclusions Grid */}
          <motion.div variants={itemVariants} className="w-full pt-10">
            <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 md:p-10 shadow-sm text-left">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 mb-8 border-b border-border">
                <div>
                  <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary uppercase tracking-wider mb-1">
                    <Sparkles className="size-3.5" /> What every monthly retainer includes
                  </div>
                  <h2 className="text-xl sm:text-2xl font-serif font-bold text-card-foreground">
                    Complete, hands-on monthly SEO management
                  </h2>
                </div>
                <Badge variant="secondary" className="px-3 py-1 text-xs self-start md:self-auto font-medium">
                  Tailored to your industry
                </Badge>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {deliverables.map((item, i) => {
                const Icon = item.icon;
                return <div key={item.title} data-index={i} className="flex flex-col p-5 rounded-xl bg-background border border-border transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-xs">
                      <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-4 shrink-0">
                        <Icon className="size-5" />
                      </div>
                      <h3 className="text-base font-semibold text-foreground mb-2 font-serif">
                        {item.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {item.description}
                      </p>
                    </div>;
              })}

                {/* Direct Consultant Promise Card */}
                <div data-index={5} className="flex flex-col justify-between p-5 rounded-xl bg-secondary/50 border border-border/80">
                  <div>
                    <div className="size-10 rounded-lg bg-primary text-primary-foreground flex items-center justify-center mb-4 shrink-0">
                      <ShieldCheck className="size-5" />
                    </div>
                    <h3 className="text-base font-semibold text-foreground mb-2 font-serif">
                      Structured monthly retainer sprints
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Every audit, technical fix, and content plan is personally researched and executed by me.
                    </p>
                  </div>
                  <div className="pt-4 mt-2 border-t border-border/60">
                    <Link to="/ContactPage" className="text-xs font-semibold text-primary inline-flex items-center gap-1 hover:underline">
                      Discuss your scope with me <ArrowRight className="size-3" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}